<script lang="ts">
  import Price from '$components/Price.svelte';
  import { STATIC_URL } from '$lib/constants';
  import { humanizeDurationHours, pluralize, toFloat, toInteger } from '$lib/utils';
  import SectionDivider from '$mds/Dividers/SectionDivider';
  import IconWrapper from '$mds/IconWrapper';
  import { ProgressiveImage, Image } from '$mds/Image';
  import { faSwords } from '@fortawesome/pro-solid-svg-icons';
  import type { SessionResult } from './SessionItem.svelte';

  export let session: SessionResult & { _formatted?: SessionResult };

  $: sessionDuration = session ? (session?.session_count || 1) * (session?.duration_seconds / 60 / 60) : 0;
  $: durationShort = humanizeDurationHours(sessionDuration, { short: true });
</script>

<div
  class=" bg-ondark-medium ease-spring-out grid h-full min-h-full scale-100 grid-rows-[min-content,1fr] overflow-hidden rounded-2xl shadow-md shadow-transparent transition-all duration-700 hover:scale-105 hover:shadow-black/20"
>
  <a
    class="flex items-center gap-2 rounded-t-lg px-4 py-2"
    href="/@{session?.seller_slug}/sessions#{session?.slug}"
    data-sveltekit-preload-code="hover"
    data-sveltekit-preload-data="off"
    title="View partner's coaching offerings"
  >
    <ProgressiveImage
      sizes={{ xs: 24 }}
      class="size-6 object-cover"
      src={session?.seller_avatar}
      alt="{session?.seller_name}'s avatar"
      rounded="rounded-full"
    />
    <span class="text-body-md highlight">
      {@html session._formatted?.seller_name ?? session?.seller_name ?? ''}
    </span>
  </a>

  <article
    class="session-card bg-ondark-medium relative grid min-h-full max-w-full grid-cols-[repeat(3,auto),1fr] grid-rows-[auto,auto,1fr,auto,auto] gap-3 rounded-2xl p-3 pt-0 transition-all"
    title={session?.name}
  >
    <a
      class="absolute left-0 top-0 z-[2] h-full w-full"
      href="/@{session?.seller_slug}/sessions/{session?.slug}"
      data-sveltekit-preload-code="hover"
      data-sveltekit-preload-data="off"
      title="View session details"
    >
      <span class="sr-only">View session details</span>
    </a>
    <header class="session-card__cover col-span-4">
      <div class="session-card__cover-image bg-ondark-medium aspect-banner overflow-hidden rounded-lg">
        <ProgressiveImage
          sizes={{ xs: 400, md: 600 }}
          aspect={5 / 2}
          class="h-full w-full object-cover"
          wrapClass="h-full w-full object-cover"
          fit="cover"
          src={session?.cover || session?.game_artwork}
          alt="Session cover image"
          draggable={false}
        />
      </div>
    </header>
    <div class="session-card__logo box-content aspect-square size-8 self-start">
      {#if session?.game_logo}
        <Image
          src={session?.game_logo}
          alt="Game logo"
          sizes={{ xs: 32 }}
          rounded="rounded-lg object-cover rounded-lg bg-ondark-high"
          class="aspect-square size-full"
          draggable={false}
        />
      {:else}
        <Image
          src="{STATIC_URL}/groups/group_logo_placeholder.png"
          sizes={{ xs: 32 }}
          class=" size-full"
          rounded="rounded-lg object-cover rounded-lg bg-ondark-high"
          draggable={false}
          alt="{session?.name} logo"
        />
      {/if}
    </div>
    <h3 class="session-card__title col-span-3 self-center">
      <p class="text-ondark-primary text-body-lg highlight line-clamp-1 break-all" title={session?.name}>
        {@html session._formatted?.name ?? session?.name ?? ''}
      </p>
    </h3>
    <div class="session-card__description col-span-4">
      <p class="text-ondark-secondary text-body-base highlight line-clamp-3">
        {@html session._formatted?.description ?? session?.description ?? ''}
      </p>
    </div>
    <div class="session-card__divider col-span-4">
      <SectionDivider variant="line" />
    </div>
    <footer class="session-card__info text-body-md col-span-3 flex items-center gap-3">
      <span class="inline-flex items-center gap-2">
        <IconWrapper theme="gradient" icon={faSwords} />
        <span class="bg-gradient-shimmer bg-clip-text text-transparent">
          {pluralize('Session', session?.session_count)}
        </span>
      </span>
      {#if session?.duration_seconds}
        <span class=" text-ondark-secondary font-normal">
          <span class="session-card__hours-short">{durationShort}</span>
        </span>
      {/if}
    </footer>
    <div class="session-card__price text-ondark-primary text-body-md items-center self-end justify-self-end px-2 py-2">
      <Price price={toFloat(session?.price)} allowFree short />
    </div>
  </article>
</div>
